import { Alert } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { formatBytes } from '~/common/file/format-bytes';
import type { MailboxQuota } from '~/server/logic/email/stably-email-client';

/**
 * Warns when an org's shared inbox is at/near its WildDuck storage quota.
 *
 * Why this exists: past quota, WildDuck's inbound SMTP handler silently rejects
 * new mail, so tests waiting on email just time out with no product signal
 * (Curebase incident, 2026-07). This banner is the "at least display it" fix.
 *
 * Renders nothing unless the mailbox is near or over quota, so callers can drop
 * it in unconditionally (pass the nullable quota straight from the query).
 */
export function MailboxQuotaAlert({
  quota,
  className,
}: {
  quota: MailboxQuota | null | undefined;
  className?: string;
}) {
  if (!quota || (!quota.isNearQuota && !quota.isOverQuota)) {
    return null;
  }

  const usage = `${formatBytes(quota.usedBytes)} of ${formatBytes(quota.allowedBytes)}`;

  return (
    <Alert
      color={quota.isOverQuota ? 'red' : 'yellow'}
      icon={<IconAlertTriangle size={18} />}
      title={quota.isOverQuota ? 'Mailbox full — incoming emails are being rejected' : 'Mailbox almost full'}
      className={className}
    >
      {quota.isOverQuota ?
        `This inbox is using ${usage}. New emails are being rejected until space is freed, so tests waiting on email will time out. Delete old emails or contact support.`
      : `This inbox is using ${usage} (${Math.round(quota.ratio * 100)}%). Once it's full, incoming emails will be rejected. Delete old emails or contact support to avoid interruptions.`}
    </Alert>
  );
}
